import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function WhereShouldIEatPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Where Should I Eat? — Restaurant Picker That Decides for You in 2 Min"
        description="Where should I eat? Let Hangrily decide. Swipe through nearby restaurants filtered by cuisine, price & mood. Works solo, with your partner, or with friends. Smarter than Google Maps scrolling or random wheels. Free, no account needed."
        path="/where-should-i-eat"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Where Should I Eat? - Restaurant Decision Tool',
            description: 'The fastest way to decide where to eat. Swipe through restaurants and match with your group.',
            url: 'https://hangrily.app/where-should-i-eat'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Where Should I Eat?', item: 'https://hangrily.app/where-should-i-eat' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I decide where to eat?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Open Hangrily, set your preferences (cuisine type, budget, distance), and start swiping through nearby restaurants. Swipe right on places that look good. In about 2 minutes, you\'ll have your answer.'
                }
              },
              {
                '@type': 'Question',
                name: 'What is the best app for deciding where to eat?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is the top restaurant decision app. Unlike random pickers or wheel spinners, it uses a swipe-based system that factors in your actual preferences. It\'s especially powerful for couples and groups who need to agree.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can Hangrily help when my partner can\'t decide?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. That\'s our most popular use case. With Couple Mode, both of you swipe independently, and Hangrily only shows restaurants you both liked. No more "I don\'t care, you pick" conversations.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily offers a free trial so you can try it out. After that, it\'s an affordable subscription for unlimited sessions. No hidden fees or surprise charges.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Where Should I Eat?</h1>
          <p className="hero-subtitle">
            The question everyone asks. The answer nobody agrees on. Until now.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Stop Scrolling. Start Swiping.</h2>
              <p>
                You've been staring at your phone for 20 minutes. Google Maps shows 47 restaurants nearby. 
                Yelp has 200 reviews for each one. Your partner says "I don't care, you pick" but vetoes 
                everything you suggest. Sound familiar?
              </p>
              <p>
                <strong>Hangrily fixes this in under 2 minutes.</strong> Instead of drowning in options, 
                we show you restaurant cards one at a time. Swipe right if it looks good, left if it doesn't. 
                If you're deciding with someone else, they swipe too — and you only match on places you 
                <em>both</em> want to try.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Decide Where to Eat Now →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">Average decision time</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Restaurant options per session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">94%</span>
                  <span className="stat-label-sm">Users happy with their match</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">0</span>
                  <span className="stat-label-sm">Arguments about food</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why "Where Should I Eat?" Is So Hard</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🧠</div>
              <h3>Decision Fatigue Is Real</h3>
              <p>
                After making hundreds of decisions throughout the day, your brain is exhausted. Research shows 
                that by dinnertime, most people have depleted their decision-making energy. That's why choosing 
                a restaurant feels impossibly hard — even when you're starving.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">😰</div>
              <h3>The Paradox of Choice</h3>
              <p>
                More options don't make choosing easier — they make it harder. When you open Google Maps and 
                see dozens of restaurants, you freeze. Hangrily eliminates this by showing you one restaurant 
                at a time, making the decision simple: yes or no.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">💑</div>
              <h3>Different Preferences, Same Hunger</h3>
              <p>
                When you're deciding with someone else, you have to factor in their cravings, dietary 
                restrictions, budget, and mood. That's why couples and groups spend an average of 30+ minutes 
                just deciding where to eat. Hangrily's matching system handles all of this automatically.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">How Hangrily Answers "Where Should I Eat?"</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Tell Us What You're Craving</h3>
                <p>
                  Select your cuisine preferences (Italian, Mexican, Thai, etc.), set your price range 
                  and distance, and pick your mood. Hangrily uses your location to find real restaurants 
                  near you — not random suggestions from across town.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Swipe Through Your Options</h3>
                <p>
                  We show you restaurant cards with photos, ratings, price levels, and distance. Swipe right 
                  to like, left to pass, or swipe up to love. It takes about 30 seconds per round — way faster 
                  than scrolling through Yelp reviews.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Get Your Perfect Match</h3>
                <p>
                  Our algorithm narrows 15+ restaurants down to THE one through up to 3 rounds. If you're 
                  swiping with your partner or group, only restaurants everyone liked make the cut. You get 
                  the winner plus runner-up options, directions, and all the details you need.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Better Than Other Ways to Decide Where to Eat</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Random Restaurant Wheel</h3>
              <p>Spins a wheel and picks randomly. No preferences. No input from your group. You might get a fast food joint when you wanted sushi.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Scrolling Google Maps / Yelp</h3>
              <p>Overwhelming number of options. Analysis paralysis. You spend 30 minutes reading reviews and still can't decide.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Asking "What Do You Want?"</h3>
              <p>"I don't care, you pick." "No, not that." "I had that yesterday." The classic dinner argument loop that never ends.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily</h3>
              <p>Personalized to your preferences. Everyone gets a vote. Smart matching finds common ground. Decision made in under 2 minutes. No arguments.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">What Should I Eat Tonight?</h2>
          <p className="section-subtitle">
            Whether you're dining solo or with a group, Hangrily works for every situation:
          </p>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Dining</h3>
              <p>Just you and your cravings? Solo mode helps you cut through decision fatigue and pick something you'll love.</p>
              <Link to="/features" className="use-case-link">Learn about Solo Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Date Night</h3>
              <p>End the "where do you want to eat?" loop forever. Both partners swipe, and you match on restaurants you both want.</p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Couples Restaurant Picker →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Group Dinner</h3>
              <p>Planning dinner with friends, family, or coworkers? Everyone joins with a code and swipes. Democratic and fair.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Restaurant Decisions →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🌆</span>
              <h3>New City</h3>
              <p>Visiting somewhere new? Hangrily pulls real local restaurants based on your location. Discover hidden gems.</p>
              <Link to="/locations" className="use-case-link">Browse by City →</Link>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I decide where to eat?</h3>
              <p>
                Open Hangrily, set your preferences (cuisine type, budget, distance), and start swiping 
                through nearby restaurants. Swipe right on places that look good. In about 2 minutes, 
                you'll have your answer.
              </p>
            </div>
            <div className="faq-item">
              <h3>What is the best app for deciding where to eat?</h3>
              <p>
                Hangrily is the top restaurant decision app. Unlike random pickers or wheel spinners, 
                it uses a swipe-based system that factors in your actual preferences. It's especially 
                powerful for couples and groups who need to agree.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can Hangrily help when my partner can't decide?</h3>
              <p>
                Absolutely. That's our most popular use case. With Couple Mode, both of you swipe 
                independently, and Hangrily only shows restaurants you both liked. No more "I don't 
                care, you pick" conversations.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is Hangrily free?</h3>
              <p>
                Hangrily offers a free trial so you can try it out. After that, it's an affordable 
                subscription for unlimited sessions. No hidden fees or surprise charges.
              </p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Stop Wondering Where to Eat</h2>
          <p>Join thousands who've ended restaurant indecision. Decide in under 2 minutes.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try Hangrily Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
